import { colors } from '@noorden-insurtech/alba-design-system';
import type { PolicyBatchPoolSummary } from '@/services/integrations';
import {
  getPolicyBatchStatusLabel,
  PolicyBatchStatus,
} from '@/shared/enums/policy-batch-status.enum';
import type { DonutChartSegment } from '@/shared/ui';
import { formatLobOption } from './policy-batch-options.util';

/**
 * Cor de cada status no donut. Mesma leitura das `Tag` da tabela
 * (`success` / `yellow` / `gray`), para o card e a listagem não divergirem.
 */
export const POLICY_BATCH_STATUS_CHART_COLORS: Record<
  PolicyBatchStatus,
  string
> = {
  [PolicyBatchStatus.Available]: colors['alba-green-500'],
  [PolicyBatchStatus.Reserved]: colors['alba-yellow-500'],
  [PolicyBatchStatus.Used]: colors['alba-gray-300'],
};

type PoolCounts = Pick<
  PolicyBatchPoolSummary,
  'available' | 'reserved' | 'used'
>;
type PoolKey = Pick<PolicyBatchPoolSummary, 'lobId' | 'channelCode'>;

/** Segmentos do donut de um pool, na ordem Disponível → Reservado → Utilizado. */
export function buildPoolSegments(pool: PoolCounts): DonutChartSegment[] {
  const entries: Array<[PolicyBatchStatus, number]> = [
    [PolicyBatchStatus.Available, pool.available],
    [PolicyBatchStatus.Reserved, pool.reserved],
    [PolicyBatchStatus.Used, pool.used],
  ];

  return entries.map(([status, value]) => ({
    key: status,
    label: getPolicyBatchStatusLabel(status),
    value,
    color: POLICY_BATCH_STATUS_CHART_COLORS[status],
  }));
}

/** `true` quando o pool está no mínimo configurado ou abaixo dele (inclui esgotado). */
export function isPoolLow(
  pool: Pick<PolicyBatchPoolSummary, 'available'>,
  minAvailable: number
): boolean {
  return pool.available <= minAvailable;
}

export function isPoolExhausted(
  pool: Pick<PolicyBatchPoolSummary, 'available'>
): boolean {
  return pool.available === 0;
}

/** Chave estável de um pool — mesma composição `lobId#channelCode` da GSI do BFF. */
export function buildPoolKey(pool: PoolKey): string {
  return `${pool.lobId}#${pool.channelCode}`;
}

/** `14 — Residencial`; ramo sem produto cadastrado fica só com o número. */
export function formatPoolTitle(
  pool: Pick<PolicyBatchPoolSummary, 'lobId'>
): string {
  return formatLobOption(pool.lobId);
}
